import { Shimmer, ShimmerElementType } from "@fluentui/react";
import { OptionOnSelectData } from "@fluentui/react-components";
import {
  GridColDef,
  GridRowSelectionModel
} from "@mui/x-data-grid";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { logEvent, logException } from "../../../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../../../helper/CustomShimmer";
import { ProductViewFiltersContext } from "../../../../pages/ProductView";
import { DATA_GRID_CONFIG } from "../../../../SharedComponents/Constants/CustomCustomerList.constants";
import { CustomDataGrid } from "../../../../SharedComponents/CustomDataGrid/CustomDataGrid";
import { useGetTopCustomersProfileByProductMutation } from "../../../../slices/ExploreProducts.slice";
import { handleToggleDataGridRowSelection } from "../../../../Utils/js-common";
import CustomDropdown from "../../Common/CustomDropdown";
import { PRODUCT_VIEW_STYLES } from "../../Common/ProductView.styles";
import { OverviewAndSummaryMutationContext } from "../../ProductView";
import DisplayAPIsException from "../../../../BasicComponents/DisplayAPIsExceptionAlert";

const TopCustomers = () => {
  const navigate = useNavigate();
  const {
    hasPartner,
    industry,
    isCAT,
    isFastTrackStrategic,
    isSST,
    productName,
    region,
    segmentGroup,
    segment,
    subSegment,
    isS500,
    isCCP,
    isTCT,
    majorGrowth
  } = useContext(ProductViewFiltersContext);

  const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>([]);
  const [paginationOptions, setPaginationOptions] = useState(DATA_GRID_CONFIG.pagination);
  const handleSelection = handleToggleDataGridRowSelection(selectionModel, setSelectionModel);
  const [selectedMetric, setSelectedMetric] = useState<string | undefined>("");
  const { data: overviewAndSummaryData } = useContext(OverviewAndSummaryMutationContext);
  const [getTopCustomers, { data, error, isError, isLoading, isSuccess }] = useGetTopCustomersProfileByProductMutation({
    fixedCacheKey: "getTopCustomers"
  });
  const cardShimmRef = useRef<any>();
  const selectedMetricRef = useRef(selectedMetric);
  const customProps = {
    productName: productName,
  };

  const columns: GridColDef[] = [
    {
      headerName: "Tenant Name",
      field: "tenantName",
      minWidth: 265,
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <a
          style={{
            color: "rgb(0, 120, 212)",
            textDecoration: "none",
            fontSize: 12,
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
          href="#"
          onClick={() => {
            navigate("/customer?id=" + params.row.tenantId + "&from=productview");
          }}
          title={params.row.tenantName}
        >
          {params.row.tenantName}
        </a>
      )
    },
    {
      headerName: "Current Value",
      field: "currentValue",
      type: "number",
      align: "left",
      headerAlign: "left",
      minWidth: 120,
      maxWidth: 200,
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <>
          {Number(parseFloat(params.row.currentValue).toFixed(2)).toLocaleString("en", {
            minimumFractionDigits: 0
          })}
        </>
      )
    },
    {
      headerName: "Region Name",
      field: "region",
      minWidth: 120,
      maxWidth: 200,
      flex: 1,
      sortable: true
    },
    {
      headerName: "Segment Group",
      field: "segmentGroup",
      minWidth: 120,
      maxWidth: 200,
      flex: 1,
      sortable: true
    },
    {
      headerName: "Sub Segment Name",
      field: "subSegment",
      minWidth: 120,
      maxWidth: 200,
      flex: 1,
      sortable: true
    },
    {
      headerName: "ProductKPIName",
      field: "productKPIName",
      minWidth: 120,
      maxWidth: 200,
      flex: 1,
      sortable: true
    }
  ];

  const showNoGraphData = () => {
    const rootStyle = {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "200px 0"
    };
    return <div style={rootStyle}>{`Table has no data to display by metric ${selectedMetric}`}</div>;
  };

  const handleSelectedMetrics = useCallback(({ optionText }: OptionOnSelectData) => {
    setSelectedMetric(optionText);
  }, []);

  const displayViewByMetricSelect = (isLoading && (
    <div style={{ flexBasis: "100%" }}>
      <Shimmer
        shimmerColors={{ shimmer: "var(--shimmer-color)" }}
        styles={{
          root: { paddingTop: 10, paddingBottom: 10, width: "25%" },
          shimmerWrapper: { ".ms-ShimmerGap-root": { background: "var(--colorBodyBackground1)" } }
        }}
        shimmerElements={[
          { type: ShimmerElementType.line, width: "15%" },
          { type: ShimmerElementType.gap, width: 10 },
          { type: ShimmerElementType.line, width: "15%" }
        ]}
      />
    </div>
  )) || (
      <CustomDropdown
        defaultOption={
          {
            optionText: data?.metricName,
            optionValue: data?.metricName,
            selectedOptions: data?.metricName ? [data?.metricName] : []
          } as OptionOnSelectData
        }
        onSelectedOption={handleSelectedMetrics}
        data={(overviewAndSummaryData?.overviewData || [])
          .filter(({ productKPIName }) => !productKPIName.includes("Tenants"))
          .map(({ productKPIName }) => ({ value: productKPIName, text: productKPIName }))}
        styles={{ root: { flexBasis: "100%", paddingBottom: 10 } }}
      />
    );

  useEffect(() => {
    selectedMetricRef.current = "";
  }, [productName]);

  useEffect(() => {
    selectedMetricRef.current = selectedMetric;
  }, [selectedMetric]);

  useEffect(() => {
    const apiBody = {
      SelectedTabName: "OpportunityTenants",
      HasPartner: hasPartner,
      Industry: industry,
      IsCAT: isCAT,
      IsFastTrackStrategic: isFastTrackStrategic,
      IsSST: isSST,
      ProductKPIName: selectedMetricRef.current,
      ProductName: productName,
      Region: region,
      SegmentGroup: segmentGroup,
      Segment: segment,
      SubSegment: subSegment,
      Vertical: "",
      isS500: isS500,
      isCCP: isCCP,
      isTCT: isTCT,
      majorGrowth: majorGrowth
    };
    getTopCustomers(apiBody);
  }, [
    hasPartner,
    industry,
    isCAT,
    isFastTrackStrategic,
    isSST,
    productName,
    region,
    segmentGroup,
    segment,
    selectedMetric,
    subSegment,
    isS500,
    isCCP,
    isTCT,
    majorGrowth
  ]);

  useEffect(() => {
    if (isSuccess) {
      logEvent("getTopCustomers API call success", "Product View Page - Top Customers Tab", "TopCustomers");
    }
    if (isError) {
      logException("Product View Page - Top Customers Tab", "TopCustomers", "getTopCustomers", error);
    }
  }, [isSuccess, isError]);

  useEffect(() => {
    logEvent("Top Customers", "Product View Page", "TopCustomers", customProps);
  }, []);

  if (!isLoading && isError) {
    return <DisplayAPIsException error={error} height={250} />;
  }

  return (
    <div className={PRODUCT_VIEW_STYLES.TAB.OPPORTUNITY_TENANTS.CLASS_NAMES.cardsContainer}>
      {displayViewByMetricSelect}
      <div ref={cardShimmRef} className={PRODUCT_VIEW_STYLES.TAB.OPPORTUNITY_TENANTS.CLASS_NAMES.cardsContainerItem}>
        {isLoading ? (
          <CustomShimmer columnCount={11} />
        ) : data?.topCustomersData && data?.topCustomersData.length > 0 ? (
          <div style={{ height: 600, width: "100%" }} tabIndex={0}>
            <CustomDataGrid
              columns={columns}
              getRowId={(row) => row.tenantId}
              paginationOptions={paginationOptions}
              setPaginationOptions={setPaginationOptions}
              onRowSelectionModelChange={handleSelection}
              rowSelectionModel={selectionModel}
              rows={{ values: data?.topCustomersData ?? [], count: data?.topCustomersData?.length ?? 0 }}
              kustoLink={data?.topCustomersProfileKustoLink}
              TabName={`Product View - Top Customers Table for ${productName}`}
            />
          </div>
        ) : (
          showNoGraphData()
        )}
      </div>
    </div>
  );
};

export default TopCustomers;
